import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle, theme } from 'styles';
import { I18nextProvider } from 'react-i18next';
import { NotFound, ScrollToTop, Loading, RouteTitle } from 'components';
import useCookie from 'hooks/useCookie';
import { Layout } from 'layout';
import { Archive, Board, BoardDetail, Community, Company, Faq, Inquiry, InquiryDetail, Main, Notice, NoticeDetail, Qna, QnaDetail, QnaWrite, Product, Technology } from 'pages';
import { krLang, enLang, selectLang } from 'lang';

import ProtectedPageWrapper from 'pages/protected-page-wrapper';
import ProfilePage from 'pages/profiles/profile';
import Intro from 'pages/intro-page';
import LoginPage from 'pages/login';

import userAuthenticationStore from 'stores/authentication-store';
import useBoardStore from 'stores/board-store';
import authenticationService from 'services/authentication/authentication-service';
import { ACCESS_TOKEN } from 'data/constants';
import { applyBoardsToLang } from 'utils/boardMenu';

import i18n from './i18n/i18n';

function App(props) {
  const { setCookies, getCookie } = useCookie();
  const [langList, setLangList] = useState();
  const [selectedLang, setSelectedLang] = useState();
  const currentRef = useRef([]);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = userAuthenticationStore((state) => state.isAuthenticated);
  const setAuthenticated = userAuthenticationStore((state) => state.setAuthenticated);
  const setUserInfo = userAuthenticationStore((state) => state.setUserInfo);

  const [isRealHost, setIsRealHost] = useState(false);

  // 관리자 게시판관리(module_board)의 게시판 목록을 한 번 조회해 커뮤니티 메뉴를 동적으로 구성
  const boards = useBoardStore((state) => state.boards);
  const fetchBoards = useBoardStore((state) => state.fetchBoards);

  useEffect(() => {
    fetchBoards();
  }, []);

  // 커뮤니티 하위 메뉴만 DB 게시판 기준으로 교체 (조회 실패 시 정적 메뉴 그대로). 한국어는 DB 게시판명, 영어는 기존 라벨 유지
  const siteLang = useMemo(
    () => applyBoardsToLang(langList, boards, !selectedLang || selectedLang.value === 'kr'),
    [langList, boards, selectedLang]
  );

  useEffect(() => {
    if (getCookie('language')) {
      console.log(krLang);
      setLangList((getCookie('language') === 'kr' && krLang) || enLang);
      setSelectedLang(selectLang.filter((el) => el.value === getCookie('language'))[0]);
    } else {
      setCookies('language', 'kr');
    }
    setLoading(false);
  }, [getCookie('language')]);

  // 최상위에서 인증 쿠키(Authorization)로 로그인 사용자(관리자 등) 정보를 자동 조회해 전역 상태에 저장
  useEffect(() => {
    authenticationService.getMyInfo()
      .then((me) => {
        if (me && me.userNo) {
          setAuthenticated(true);
          setUserInfo(me);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    async function fetchMeData() {
      const userInfo = await authenticationService.getMyInfo();

      if (userInfo && userInfo.id) {
        setAuthenticated(true);
        setUserInfo(userInfo)
;      }
    }

    if (!isAuthenticated) {
      fetchMeData()
    }
  }, []);

  // useEffect(() => {
  //   if (window.location.host === 'www.farm-in.kr'
  //       || window.location.host === 'farm-in.kr') {
  //     setIsRealHost(true);
  //   }
  // }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <CookiesProvider>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          {(loading && <Loading />) || (
            <BrowserRouter>
              <ScrollToTop>
                <RouteTitle />
                {
                  isRealHost ?
                  <Routes>
                    <Route path="/" element={<Intro />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/profile" element={
                      <ProtectedPageWrapper>
                        <ProfilePage />
                      </ProtectedPageWrapper>
                    }/>
                  </Routes>
                  :
                  <Routes>
                    <Route path="*" element={<NotFound />} />
                    <Route element={<Layout setRef={currentRef} langList={siteLang} selectedLang={selectedLang} />}>
                      <Route path="/" element={<Main setRef={currentRef} langList={siteLang} />} />
                      <Route path="/company" element={<Company setRef={currentRef} langList={siteLang} />} />
                      <Route path="/product" element={<Product setRef={currentRef} langList={siteLang} />} />
                      <Route path="/technology" element={<Technology setRef={currentRef} langList={siteLang} />} />
                      <Route path="/community" element={<Community setRef={currentRef} langList={siteLang} />}>
                        <Route path="notice" element={<Notice />} />
                        <Route path="notice/view" element={<NoticeDetail />} />
                        <Route path="inquiry" element={<Inquiry />} />
                        <Route path="inquiry/view" element={<InquiryDetail />} />
                        <Route path="faq" element={<Faq />} />
                        <Route path="qna" element={<Qna />} />
                        <Route path="qna/view" element={<QnaDetail />} />
                        <Route path="qna/write" element={<QnaWrite />} />
                        <Route path="qna/modify" element={<QnaWrite />} />
                        <Route path="archive" element={<Archive />} />
                        {/* 관리자 게시판관리에서 추가한 게시판 (전용 페이지 없음) */}
                        <Route path="board/:moduleNo" element={<Board />} />
                        <Route path="board/:moduleNo/view" element={<BoardDetail />} />
                      </Route>
                    </Route>
                  </Routes>
                }
              </ScrollToTop>
            </BrowserRouter>
          )}
        </ThemeProvider>
      </CookiesProvider>
    </I18nextProvider>
  );
}

export default App;
